import React from "react";
import { WorkflowExecution } from "@/types/workflow";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ExecutionStatusBadge } from "./ExecutionStatusBadge";
import { formatDistanceToNow, format } from "date-fns";
import { nl } from "date-fns/locale";
import { Eye, Ban, Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface ExecutionListProps {
  executions: WorkflowExecution[];
  isLoading: boolean;
  onViewExecution: (execution: WorkflowExecution) => void;
  onCancelExecution?: (executionId: string) => void;
  isCancelling?: boolean;
}

export const ExecutionList: React.FC<ExecutionListProps> = ({
  executions,
  isLoading,
  onViewExecution,
  onCancelExecution,
  isCancelling = false,
}) => {
  const formatDuration = (startedAt: string, completedAt: string | null) => {
    if (!completedAt) return "-";
    const durationMs = new Date(completedAt).getTime() - new Date(startedAt).getTime();
    if (durationMs < 1000) return `${durationMs}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
    return `${(durationMs / 60000).toFixed(1)}m`;
  };

  const canCancel = (status: string) => 
    ["pending", "running", "waiting"].includes(status);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  if (executions.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>Geen uitvoeringen gevonden</p>
        <p className="text-sm mt-1">Pas de filters aan of wacht op nieuwe uitvoeringen</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead>Workflow</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Gestart</TableHead>
            <TableHead>Duur</TableHead>
            <TableHead className="text-right">Acties</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {executions.map((execution) => (
            <TableRow 
              key={execution.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onViewExecution(execution)}
            >
              <TableCell className="font-medium">
                <div>
                  <p className="truncate max-w-[200px]">
                    {execution.workflow?.name || "Onbekende workflow"}
                  </p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {execution.id.slice(0, 8)}...
                  </p>
                </div>
              </TableCell>
              <TableCell>
                <ExecutionStatusBadge status={execution.status} size="sm" />
              </TableCell>
              <TableCell>
                <div>
                  <p className="text-sm">
                    {formatDistanceToNow(new Date(execution.started_at), {
                      addSuffix: true,
                      locale: nl,
                    })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(execution.started_at), "dd MMM HH:mm:ss", { locale: nl })}
                  </p>
                </div>
              </TableCell>
              <TableCell>
                {formatDuration(execution.started_at, execution.completed_at)}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewExecution(execution)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  {canCancel(execution.status) && onCancelExecution && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => onCancelExecution(execution.id)}
                      disabled={isCancelling}
                    >
                      {isCancelling ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Ban className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
